import { useState, useEffect } from 'react'
import { CheckIcon, XMarkIcon, EyeIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'
import WithdrawalDetailModal from '@/components/modals/WithdrawalDetailModal'
import { TablePagination } from '@/components/ui/ResponsiveTable'

const PAGE_SIZE = 20

export default function WithdrawalsPage() {
  const [withdrawals, setWithdrawals] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [currencyFilter, setCurrencyFilter] = useState('all')
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchWithdrawals(currentPage)
  }, [filter, currencyFilter, currentPage])

  const fetchWithdrawals = async (page = currentPage) => {
    try {
      setLoading(true)
      
      // Only include params if they're not 'all'
      const params = {}
      if (filter !== 'all' && filter !== '') {
        params.status = filter
      }
      if (currencyFilter !== 'all' && currencyFilter !== '') {
        params.currency = currencyFilter
      }
      params.page = page
      params.limit = PAGE_SIZE
      
      const response = await api.get('/withdrawals', {
        params
      })
      
      if (response.data.success) {
        const { withdrawals: items = [], pagination = {} } = response.data.data || {}
        const resolvedTotalPages = Math.max(1, pagination.totalPages || 1)

        setTotalPages(resolvedTotalPages)

        if (page > resolvedTotalPages) {
          if (currentPage !== resolvedTotalPages) {
            setCurrentPage(resolvedTotalPages)
          }
          return
        }

        setWithdrawals(items)

        const resolvedPage = pagination.page || page
        if (resolvedPage !== currentPage) {
          setCurrentPage(resolvedPage)
        }
      }
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateWithdrawalStatus = async (withdrawalId, status, adminNotes = null) => {
    try {
      let confirmMessage = ''
      let successMessage = ''
      
      switch (status) {
        case 'approved':
          confirmMessage = '이 출금 요청을 승인하시겠습니까?'
          successMessage = '출금 요청이 승인되었습니다.'
          break
        case 'rejected':
          confirmMessage = '이 출금 요청을 거부하시겠습니까?'
          successMessage = '출금 요청이 거부되었습니다.'
          break
        case 'pending':
          confirmMessage = '이 출금을 대기중으로 되돌리시겠습니까?'
          successMessage = '출금이 대기중으로 변경되었습니다.'
          break
        default:
          confirmMessage = '출금 상태를 변경하시겠습니까?'
          successMessage = '출금 상태가 변경되었습니다.'
      }
      
      if (!confirm(confirmMessage)) return

      const requestData = { status }
      if (adminNotes) {
        requestData.adminNotes = adminNotes
      }
      
      console.log('Sending request:', requestData)
      await api.put(`/withdrawals/${withdrawalId}/status`, requestData)

      // Refresh the withdrawals list
      fetchWithdrawals()
      
      alert(successMessage)
    } catch (error) {
      console.error('Failed to update withdrawal status:', error)
      alert('출금 상태 업데이트에 실패했습니다.')
    }
  }

  const getStatusLabel = (status) => {
    const labels = {
      'pending': '대기중',
      'approved': '승인됨',
      'rejected': '거절됨',
      'completed': '완료됨'
    }
    return labels[status] || status
  }

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'completed': 'bg-gray-100 text-gray-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const handleStatusFilterChange = (value) => {
    setFilter(value)
    setCurrentPage(1)
  }

  const handleCurrencyFilterChange = (value) => {
    setCurrencyFilter(value)
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">출금 관리</h1>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="space-y-4">
          {/* Status Filter */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">상태별 필터</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: '전체' },
                { key: 'pending', label: '대기중' },
                { key: 'approved', label: '승인됨' },
                { key: 'rejected', label: '거절됨' },
                { key: 'completed', label: '완료됨' }
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleStatusFilterChange(item.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    filter === item.key
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          {/* Currency Filter */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">통화별 필터</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: '전체' },
                { key: 'krw', label: '원화' },
                { key: 'token', label: '토큰' }
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleCurrencyFilterChange(item.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    currencyFilter === item.key
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Withdrawals Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                사용자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                금액
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                통화
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                은행/지갑정보
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                예금주/주소
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                처리자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                신청일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {withdrawals.map((withdrawal) => (
              <tr key={withdrawal.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  #{withdrawal.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {withdrawal.user?.name || '정보없음'}
                  <div className="text-xs text-gray-500">
                    @{withdrawal.user?.username || '정보없음'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {Number(withdrawal.amount).toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {withdrawal.currency === 'krw' ? '원화' : '토큰'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {withdrawal.currency === 'krw' 
                    ? withdrawal.withdrawalInfo?.bankName || '-'
                    : '토큰 지갑'
                  }
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {withdrawal.currency === 'krw' 
                    ? (
                      <div>
                        <div className="font-medium">{withdrawal.withdrawalInfo?.accountHolder || '-'}</div>
                        <div className="text-xs text-gray-500">{withdrawal.withdrawalInfo?.accountNumber || '-'}</div>
                      </div>
                    ) : (
                      <div className="font-mono text-xs break-all">
                        {withdrawal.withdrawalInfo?.walletAddress || '-'}
                      </div>
                    )
                  }
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(withdrawal.status)}`}>
                    {getStatusLabel(withdrawal.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {withdrawal.processedByAdmin ? (
                    <div>
                      <div>{withdrawal.processedByAdmin.name || '관리자'}</div>
                      <div className="text-xs text-gray-500">@{withdrawal.processedByAdmin.username}</div>
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(withdrawal.createdAt).toLocaleDateString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        setSelectedWithdrawal(withdrawal)
                        setShowDetailModal(true)
                      }}
                      className="text-blue-600 hover:text-blue-900"
                      title="상세보기"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    {withdrawal.status === 'pending' && (
                      <>
                        <button 
                          onClick={() => updateWithdrawalStatus(withdrawal.id, 'approved')}
                          className="text-green-600 hover:text-green-900"
                          title="승인"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => updateWithdrawalStatus(withdrawal.id, 'rejected')}
                          className="text-red-600 hover:text-red-900"
                          title="거부"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {withdrawal.status === 'approved' && (
                      <button 
                        onClick={() => updateWithdrawalStatus(withdrawal.id, 'pending')}
                        className="text-orange-600 hover:text-orange-900"
                        title="대기중으로 되돌리기"
                      >
                        <ArrowUturnLeftIcon className="h-4 w-4" />
                      </button>
                    )}
                    {withdrawal.status === 'rejected' && (
                      <button 
                        onClick={() => updateWithdrawalStatus(withdrawal.id, 'pending')}
                        className="text-orange-600 hover:text-orange-900"
                        title="대기중으로 되돌리기"
                      >
                        <ArrowUturnLeftIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {withdrawals.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">출금 요청이 없습니다.</p>
        </div>
      )}

      {withdrawals.length > 0 && totalPages > 1 && (
        <div className="flex justify-end">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            showPageNumbers
          />
        </div>
      )}

      {/* Detail Modal */}
      <WithdrawalDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false)
          setSelectedWithdrawal(null)
        }}
        withdrawal={selectedWithdrawal}
      />
    </div>
  )
}
